import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import PhoneInput from "react-phone-input-2";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { ContactEmail, ContactLocation } from "../../assets";
import { EMPTY_STRING } from "../../constants";
import { SEND_MESSAGE } from "../../constants/contactUs.constants";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import { contactUsActions } from "../../redux/actions";
import Topbar from "../../layout/Topbar";
import Footer from "../../layout/Footer";

// Yup validation schema
const schema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup
    .string()
    .required("Email is required")
    .max(100, "Email must not exceed 100 characters")
    .test(
      "is-valid-email",
      "Invalid email address",
      (value) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ),
  phoneNumber: yup.string().required("Phone Number is required"),
  message: yup.string().required("Message is required"),
});

const ContactUs = ({ contactUs, spinnerActions }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      email: EMPTY_STRING,
      phoneNumber: EMPTY_STRING,
      firstName: EMPTY_STRING,
      lastName: EMPTY_STRING,
      message: EMPTY_STRING,
    },
  });

  const [phoneLength, setPhoneLength] = useState(0);

  // Handle form submission
  const onSubmit = (data) => {
    contactUs(data, () => {
      reset();
    });
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  useEffect(() => {
    if (watch("phoneNumber")) {
      if (
        phoneLength > watch("phoneNumber")?.length &&
        watch("phoneNumber")?.length < 15
      ) {
        setError("phoneNumber", {
          type: "minLength",
          message: "Invalid phone number",
        });
      } else {
        clearErrors("phoneNumber");
      }
    }

    // eslint-disable-next-line
  }, [watch("phoneNumber")]);

  return (
    <>
      <Topbar />
      {spinner(SEND_MESSAGE) > -1 && <MainSpinner />}
      <MainContentWrapper>
        <div className="container">
          <div className="about-us-heading">Contact Us</div>
          <div className="d-flex flex-wrap contact-us-form-container">
            <div className="contact-us-form">
              <div className="text-center from-heading">Contact Form</div>
              <div className="contact-fields">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="field-container">
                    <label className="field-label">First Name:</label>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          {...field}
                          className="field"
                          placeholder="First name"
                          autocomplete="off"
                        />
                      )}
                    />
                    {errors.firstName && (
                      <p className="error">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div className="field-container">
                    <label className="field-label">Last Name:</label>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          {...field}
                          className="field"
                          placeholder="Last name"
                          autocomplete="off"
                        />
                      )}
                    />
                    {errors.lastName && (
                      <p className="error">{errors.lastName.message}</p>
                    )}
                  </div>
                  <div className="field-container">
                    <label className="field-label">Email Address</label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          {...field}
                          className="field"
                          placeholder="Email address"
                          autocomplete="off"
                        />
                      )}
                    />
                    {errors.email && (
                      <p className="error">{errors.email.message}</p>
                    )}
                  </div>
                  <div className="field-container phone-number-field">
                    <label className="field-label">Phone Number:</label>
                    <Controller
                      name="phoneNumber"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <PhoneInput
                          {...field}
                          placeholder="(916) 687-9606"
                          country={"us"}
                          inputExtraProps={{
                            required: true,
                            autoFocus: true,
                          }}
                          onChange={(phone, country) => {
                            setValue("phoneNumber", phone);
                            setPhoneLength(
                              country.format
                                .split("")
                                .filter((char) => char === ".").length
                            );
                          }}
                          masks={{
                            sa: ".. ... ....",
                            tr: "... ... ....",
                            eg: ".. .... ....",
                          }}
                        />
                      )}
                    />
                    {errors.phoneNumber && (
                      <p className="error">{errors.phoneNumber.message}</p>
                    )}
                  </div>
                  <div className="field-container">
                    <label className="field-label">Message:</label>
                    <Controller
                      name="message"
                      control={control}
                      render={({ field }) => (
                        <textarea
                          {...field}
                          className="field"
                          placeholder="Message"
                          onResize="none"
                          rows={5}
                        />
                      )}
                    />
                    {errors.message && (
                      <p className="error">{errors.message.message}</p>
                    )}
                  </div>
                  <div className="d-flex justify-content-center w-100 add-admin-button">
                    <RoundedButton type="submit" className="px-5 py-2">
                      Submit
                    </RoundedButton>
                  </div>
                </form>
              </div>
            </div>
            <div className="lets-talk-with-us">
              <div className="lets-talk-heading">Lets's talk with us</div>
              <div className="lets-talk-statement">
                Questions, comments, or suggestions? Simply fill in the form and
                we’ll be in touch shortly.
              </div>
              <div className="lets-talk-email d-flex gap-2 align-items-center">
                <img src={ContactEmail} alt={EMPTY_STRING} />
                <span>
                  <a href="mailto:letstalk@bayviewshores.com">
                    letstalk@bayviewshores.com
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </MainContentWrapper>
    </>
  );
};
const mapStateToProps = (state) => ({
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  contactUs: (body, func) => dispatch(contactUsActions.contactUs(body, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContactUs);
